<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="realtors">
					<app-heading :title="`${market.name} Realtors`">
						<template v-slot:left>
							<app-button
								v-if="$ravello.can('marketmath.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								small
								outlined
								:to="{ name: 'market-realtor', params: { realtor: 'new' } }"
							>Add Realtor</app-button>
						</template>
						<template v-slot:right>
							<input-file
								type="csv"
								button
								small
								outlined
								label="Import (CSV)"
								@load="onImport"
							/>
							<app-button
								button
								small
								outlined
								color="secondary"
								icon="fa-download"
								@click="exportBrokeragesResults"
							>Export Brokerages (V2)</app-button>
							<app-button
								button
								small
								outlined
								color="secondary"
								icon="fa-download"
								@click="exportV2Realtors"
							>Export (V2)</app-button>
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<app-data-table
								:items="realtors"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								:initial-state="tableState"
								search
								sort-by="name"
								@search="onSearch"
								@update="fetchRealtors"
							>
								<template v-slot:actions="{ item }">
									<app-button
										v-if="$ravello.can('marketmath.put')"
										color="secondary"
										icon="fa-user-friends"
										table
										@click="onMerge(item)"
									>
										Merge
									</app-button>
									<app-button
										v-if="$ravello.can('marketmath.put')"
										color="secondary"
										:to="{ name: 'market-realtor', params: { realtor: item._id } }"
										icon="fa-pencil-alt"
										table
									>
										Edit
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('marketmath.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove realtor <b>${item.name}</b>?`"
										title="Remove Realtor"
										@click="onDelete(item._id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'market-realtor', params: { realtor: 'new' } }"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
			</template>
		</app-tabs>

		<app-dialog
			v-model="mergeDialog.show"
			:title="`Merging ${mergeDialog.name}`"
			max-width="320"
		>
			<app-dialog-body>
				<div><strong>
					Merging this realtor with another will delete this realtor and move all listings
					under the chosen realtor
				</strong></div>
				<input-combobox
					v-model="mergeDialog.value"
					:items="mergeDialog.realtors"
					label="Realtor"
				/>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="warning"
					flat="flat"
					@click="merge"
					:disabled="!mergeDialog.value"
				>
					Merge
				</app-button>
				<app-button
					flat="flat"
					@click="mergeDialog.show = false"
				>
					Cancel
				</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
import { filters, search, tableState } from '@/mixins'
import { marketMathTabs } from '@/datasources/tabs'
import { importCollection2, exportCollection2 } from '@/utils/import'


const IMPORT_FIELDS = ['name', 'email', 'phone', 'office_phone', 'brokerage', 'avatar', 'years_experience']
export default {
	mixins: [filters, search, tableState],
	data() {
		return {
			market: {
				name: '',
			},
			realtors: [],
			count: -1,
			refresh: false,
			tabs: [],
			headers: [{
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Email',
				value: 'email',
				sortable: true,
			}, {
				text: 'Phone',
				value: 'phone',
				sortable: true,
			}, {
				text: 'Brokerage',
				value: 'brokerage',
				sortable: true,
			}, {
				text: 'Is Subscribed?',
				value: 'subscribed',
				sortable: true,
				cb: item => (item.subscribed ? 'Yes' : 'No'),
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
			mergeDialog: {
				show: false,
				realtors: [],
				id: null,
				name: '',
				value: null,
			},
			defaultTableSort: 'name',
		}
	},
	computed: {
		marketId() {
			return this.$route.params.market
		},
	},
	async mounted() {
		this.market = await this.$ravello.graphql('market', this.marketId)
			.fields(['name'])
			.query()

		this.tabs = marketMathTabs(this.marketId)
	},
	methods: {
		async fetchRealtors(pagination) {
			this.updateTableState(pagination)

			const request = this.$ravello.graphql('realtors')
				.parent(this.$ravello.graphql('market', this.marketId))
				.fields(['_id', 'name', 'email', 'phone', 'brokerage', 'subscribed'])
				.fromPagination(pagination)

			if (this.count === -1 && !this.searchString) {
				request.count('count')
			}

			if (this.searchText) {
				request.search(this.searchText)
			}
			try {
				this.realtors = await request.query()

				if (this.searchText) {
					this.count = this.realtors.length
				}
				if (this.count === -1) {
					this.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onMerge({ _id: id, name }) {
			this.$store.dispatch('app/startLoading')

			try {
				this.mergeDialog.realtors = await this.$ravello.graphql('realtors')
					.parent(this.$ravello.graphql('market', this.marketId))
					.filter('_id', { ne: id })
					.datasource()
					.sort('name')
					.query()

				this.mergeDialog.id = id
				this.mergeDialog.name = name
				this.mergeDialog.value = null
				this.mergeDialog.show = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('realtors')
					.to(id)
					.delete()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async merge() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('realtors')
					.to(this.mergeDialog.value)
					.to('merge')
					.create({
						realtor: this.mergeDialog.id,
					})
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.mergeDialog.show = false
			this.$store.dispatch('app/stopLoading')
		},

		async onImport(importData) {
			this.$store.dispatch('app/startLoading')

			const request = this.$ravello.request()
				.v(2)
				.to('markets')
				.to(this.marketId)
				.to('realtors')

			const validImportData = importData.filter(data => data.name && data.name !== '')
				.map(data => IMPORT_FIELDS.reduce((acc, field) => ({
					...acc,
					...(typeof data[field] !== 'undefined' ? { [field]: data[field] } : {}),
				}), {}))
			try {
				await importCollection2(validImportData, request, { upsert: true })
				this.refresh = 'hard'
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async exportV2Realtors() {
			const request = this.$ravello.graphql('realtors')
				.parent(this.$ravello.graphql('market', this.marketId))
				.fields(['_id', 'name', 'email', 'phone', 'office_phone', 'brokerage',
					'avatar', 'years_experience', 'aliases', 'email_alt'])

			await exportCollection2('realtors', 'json', request, {
				graphql: true,
				map: entry => ({
					legacyId: entry._id,
					name: entry.name,
					phone: entry.phone,
					officePhone: entry.office_phone,
					email: entry.email,
					aliases: entry.aliases,
					alternateEmails: entry.email_alt,
					yearsOfExperience: entry.years_experience,
					avatar: entry.avatar,
					brokerageName: entry.brokerage,
				}),
			})
		},

		async exportBrokeragesResults() {
			const request = this.$ravello.graphql('realtors')
				.parent(this.$ravello.graphql('market', this.marketId))
				.fields(['brokerage'])

			await exportCollection2('brokerages', 'json', request, {
				graphql: true,
				map: entry => ({
					name: entry.brokerage,
				}),
			})
		},
	},
}
</script>
